import { useState, useEffect } from 'react';

const BREAKPOINTS = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
};

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches);
    const handler = (e) => setMatches(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [query]);

  return matches;
};

/**
 * Hook to get responsive breakpoint information
 * @returns {Object} Object containing breakpoint booleans and current breakpoint
 */
export const useResponsive = () => {
  const isXs = useMediaQuery(`(max-width: ${BREAKPOINTS.sm - 1}px)`);
  const isSm = useMediaQuery(`(min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md - 1}px)`);
  const isMd = useMediaQuery(`(min-width: ${BREAKPOINTS.md}px) and (max-width: ${BREAKPOINTS.lg - 1}px)`);
  const isLg = useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px)`);

  const isMobile = useMediaQuery(`(max-width: ${BREAKPOINTS.sm - 1}px)`);
  const isTablet = useMediaQuery(`(min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md - 1}px)`);
  const isDesktop = useMediaQuery(`(min-width: ${BREAKPOINTS.md}px)`);
  const isMobileOrTablet = useMediaQuery(`(max-width: ${BREAKPOINTS.md - 1}px)`);

  return {
    isXs,
    isSm,
    isMd,
    isLg,
    isMobile,
    isTablet,
    isDesktop,
    isMobileOrTablet,
  };
};

/**
 * Hook to check if current viewport is mobile
 * @returns {boolean} True if mobile viewport
 */
export const useIsMobile = () => {
  return useMediaQuery(`(max-width: ${BREAKPOINTS.sm - 1}px)`);
};

/**
 * Hook to check if current viewport is tablet
 * @returns {boolean} True if tablet viewport
 */
export const useIsTablet = () => {
  return useMediaQuery(`(min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md - 1}px)`);
};

/**
 * Hook to check if current viewport is desktop
 * @returns {boolean} True if desktop viewport
 */
export const useIsDesktop = () => {
  return useMediaQuery(`(min-width: ${BREAKPOINTS.md}px)`);
};
